import { ColumnDef, Table } from '@tanstack/react-table';
import { IconButton } from 'src/components/Buttons';
import { Link } from 'src/components/Metrics/MetricTile/MetricTile';
import { BaseCell } from 'src/components/TableV2/cells';
import { Content } from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { CopyToClipboardV2 } from 'src/core/ui/CopyToClipboard/CopyToClipboardV2';
import { CopyIcon2 } from 'src/svgs/CopyIcon2';
import { TableShimmeringDiv } from 'src/tables/Table';
import { ContentId } from 'src/utils/constants/contentId';

import { SHIMMERING_DIV_HEIGHT_SINGLE_SALE } from '../../SalesTable/SalesTable.type';

const COLUMN_MIN_SIZE = 50;
const COLUMN_MAX_SIZE = 500;

interface TextColumnOptions<TData> {
  /** Column ID */
  id: string;
  /** Content ID for the header label */
  headerContentId: string;
  /** Accessor function to get the text value from data */
  accessor: (data: TData) => string | number | boolean | null | undefined;
  /** Column size */
  size?: number;
  /** Min size */
  minSize?: number;
  /** Max size */
  maxSize?: number;
  /** Text alignment */
  align?: 'left' | 'right' | 'center';
  /** Enable column filter */
  enableColumnFilter?: boolean;
  /** Custom filter function */
  filterFn?: any;
  /** Custom sorting function */
  sortingFn?: any;
  /** Custom cell renderer - can be a function or React component */
  customCellRenderer?:
    | ((props: {
        value: string | number | boolean | null | undefined;
        original: TData;
        columnId: string;
        table: Table<TData>;
        defaultRenderer: () => React.ReactNode;
      }) => React.ReactNode)
    | React.ComponentType<{
        value: string | number | boolean | null | undefined;
        original: TData;
        columnId: string;
        table: Table<TData>;
        defaultRenderer: () => React.ReactNode;
      }>;
  /** Include footer with aggregated totals */
  withFooter?: boolean;
  /** Custom footer renderer */
  customFooterRenderer?: (props: {
    value: string | number | boolean | null | undefined;
    columnId: string;
  }) => React.ReactNode;
  /** Function to determine if cell should render (return false to render null) */
  shouldRender?: (data: TData, table: Table<TData>) => boolean;
  meta?: Record<string, any>;
}

/**
 * Factory function to create text column definitions for tables.
 * Handles simple text/string display with optional custom rendering.
 *
 * @example
 * createTextColumn({
 *   id: SalesTableColumnId.PaymentReferenceId,
 *   headerContentId: SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.PaymentReferenceId],
 *   accessor: (data) => data?.sale?.pmtRefId,
 *   size: COLUMN_DEFAULT_SIZE_LG,
 * })
 */
export const createCopyColumn = <TData,>(
  options: TextColumnOptions<TData>
): ColumnDef<TData> => {
  const {
    id,
    headerContentId,
    accessor,
    size,
    minSize = COLUMN_MIN_SIZE,
    maxSize = COLUMN_MAX_SIZE,
    enableColumnFilter = true,
    filterFn,
    sortingFn,
    shouldRender,
    meta,
  } = options;

  return {
    id,
    enableColumnFilter,
    ...(filterFn && { filterFn }),
    ...(sortingFn && { sortingFn }),
    size,
    minSize,
    maxSize,
    header: () => <Content id={headerContentId} />,
    accessorFn: accessor,
    cell: function Cell({ getValue, row: { original }, table }) {
      const value = getValue<string>();

      if (!original) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      // Check if cell should be rendered
      if (shouldRender && !shouldRender(original, table)) {
        return null;
      }

      if (!value) {
        return <BaseCell />;
      }

      return (
        <BaseCell
          // {...props}
          isFullWidth={true}
          showPadding={false}
          style={{ minWidth: 0 }}
        >
          <Stack
            justifyContent="spaceBetween"
            alignItems="center"
            style={{ width: '100%', minWidth: 0 }}
          >
            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <CopyToClipboardV2
                textToCopy={value}
                displayCopyIcon={false}
                style={{ minWidth: 0, overflow: 'hidden' }}
                wrapperStyle={{
                  display: 'block',
                  minWidth: 0,
                  overflow: 'hidden',
                  width: '100%',
                }}
              >
                <Stack justifyContent="spaceBetween">
                  {value && (
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                        width: '100%',
                        color: vars.color.textBrandV2,
                        minWidth: 0,
                        textTransform: 'lowercase',
                      }}
                    >
                      {value}
                    </span>
                  )}
                  <IconButton
                    style={{
                      padding: vars.spacing.xxs,
                      backgroundColor: vars.color.surfaceSecondary,
                    }}
                    icon={
                      <CopyIcon2
                        size={vars.iconSize.s}
                        withHoverEffect
                        stroke={vars.color.iconBrand}
                        fill="transparent"
                      />
                    }
                    titleContentId={ContentId.CopyEmailAddress}
                  />
                </Stack>
              </CopyToClipboardV2>
            </div>
          </Stack>
        </BaseCell>
      );
    },
    meta: {
      columnFilterVariant: 'multiSelect',
      ...meta,
    },
  };
};
